import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import classNames from 'classnames';
import {
  FLOW_METRICS_INTERVAL_LATEST_RUN,
  FLOW_METRICS_INTERVAL_LIFETIME,
  FLOW_METRICS_INTERVAL_LAST_24H,
  FLOW_METRICS_INTERVAL_TODAY,
  FLOW_METRICS_INTERVAL_LAST_7_DAYS,
  FLOW_METRICS_INTERVAL_LAST_15_DAYS,
} from 'js/constants/metricsConstants';
import classes from './MetricsIntervalSelector.module.scss';

export type MetricsInterval =
  | typeof FLOW_METRICS_INTERVAL_LIFETIME
  | typeof FLOW_METRICS_INTERVAL_LATEST_RUN
  | typeof FLOW_METRICS_INTERVAL_LAST_24H
  | typeof FLOW_METRICS_INTERVAL_TODAY
  | typeof FLOW_METRICS_INTERVAL_LAST_7_DAYS
  | typeof FLOW_METRICS_INTERVAL_LAST_15_DAYS;

// Human labels for every interval value. The selector renders whichever `options`
// the caller passes (batch toggle vs. continuous window set), so one component covers both.
const INTERVAL_LABELS: Record<string, string> = {
  [FLOW_METRICS_INTERVAL_LATEST_RUN]: 'Latest Run',
  [FLOW_METRICS_INTERVAL_LIFETIME]: 'Lifetime',
  [FLOW_METRICS_INTERVAL_LAST_24H]: 'Last 24h',
  [FLOW_METRICS_INTERVAL_TODAY]: 'Today',
  [FLOW_METRICS_INTERVAL_LAST_7_DAYS]: 'Last 7 days',
  [FLOW_METRICS_INTERVAL_LAST_15_DAYS]: 'Last 15 days',
};

// Per-option hover hint, shown on each segment. "Last 24h" is rolling (now − 24h → now),
// so it carries no timezone qualifier — it is the same window for every user.
const INTERVAL_TOOLTIPS: Record<string, string> = {
  [FLOW_METRICS_INTERVAL_LATEST_RUN]: 'Metrics from the most recent run',
  [FLOW_METRICS_INTERVAL_LIFETIME]: 'All-time totals',
  [FLOW_METRICS_INTERVAL_LAST_24H]: 'Rolling last 24 hours',
  [FLOW_METRICS_INTERVAL_TODAY]: 'Today (UTC)',
  [FLOW_METRICS_INTERVAL_LAST_7_DAYS]: 'Last 7 days (UTC)',
  [FLOW_METRICS_INTERVAL_LAST_15_DAYS]: 'Last 15 days (UTC)',
};

interface MetricsIntervalSelectorProps {
  /** The interval values to render, in display order (e.g. BATCH_METRICS_INTERVALS or CONTINUOUS_METRICS_INTERVALS). */
  options: string[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const MetricsIntervalSelector = ({
  options,
  value,
  onChange,
  className,
}: MetricsIntervalSelectorProps) => {
  const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue !== null && newValue !== value) {
      onChange(newValue);
    }
  };

  return (
    <ToggleButtonGroup
      className={classNames(classes.buttonGroup, className)}
      value={value}
      exclusive
      onChange={handleChange}
      aria-label="Metric time window"
      size="small"
    >
      {options.map((interval) => (
        // Native `title` (not MUI <Tooltip>) so each ToggleButton stays the direct child of
        // ToggleButtonGroup — the group clones its immediate children to wire up value/selection,
        // and a wrapper element would swallow that. The group carries the aria-label.
        <ToggleButton
          key={interval}
          title={INTERVAL_TOOLTIPS[interval] ?? undefined}
          className={classNames(classes.button, {
            [classes.buttonActive]: value === interval,
          })}
          value={interval}
        >
          {INTERVAL_LABELS[interval] ?? interval}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
